import { Button, Card, Input } from 'antd';
import { CardTitle } from './CardDataDisplay';
import { useTranslation } from '../../../i18n/i18n';
import FieldItem from './FieldItem';
import './OtherFieldsCard.scss';
import { DeleteOutlined, PlusOutlined } from '@ant-design/icons';
import { colors } from '../../../theme/colors';
import { useEffect, useState } from 'react';
import useAuthStore from '../../../stores/auth.store';

interface Field {
  title: string;
  value: string;
}

interface OtherFieldsCardProps {
  fields: Field[];
  onSave: (fields: Field[]) => Promise<void>;
  adminUuid?: string;
}

const OtherFieldsCard = ({ fields, onSave, adminUuid }: OtherFieldsCardProps) => {
  const { t } = useTranslation('installation-location', {
    keyPrefix: 'installationLocationDetail.installation.summary.otherFields',
  });
  const [localFields, setLocalFields] = useState<Field[]>(fields);
  const [newFields, setNewFields] = useState<{ id: string; title: string; value: string }[]>([]);
  const [saving, setSaving] = useState(false);
  const isSuperAdminOrCompanyAdmin = useAuthStore((state) => state.isSuperAdminOrCompanyAdmin);
  const isAdmin = useAuthStore((state) => state.isAdmin);
  const userUuid = useAuthStore((state) => state.uuid);

  const isAdminLocation = isAdmin && adminUuid === userUuid;

  useEffect(() => {
    setLocalFields(fields);
  }, [fields]);

  const handleSave = async () => {
    setSaving(true);
    try {
      const allFields = [
        ...localFields,
        ...newFields.filter((f) => f.title.trim()).map(({ title, value }) => ({ title, value })),
      ];
      await onSave(allFields);
      setNewFields([]);
    } catch {
      // error message shown by handleSaveInstallation
    } finally {
      setSaving(false);
    }
  };

  return (
    <Card className={'OtherFieldsCard'}>
      <CardTitle title={t('title')} />
      <div className={'OtherFiledsCard__content'}>
        {localFields.map((field, index) => (
          <FieldItem
            key={`${field.title}-${index}`}
            title={field.title}
            value={field.value}
            adminUuid={adminUuid}
            onChange={(val) => setLocalFields((prev) => prev.map((f, i) => (i === index ? { ...f, value: val } : f)))}
            onDelete={async () => {
              const previous = localFields;
              const remaining = localFields.filter((_, i) => i !== index);
              setLocalFields(remaining);
              setSaving(true);
              try {
                await onSave(remaining);
              } catch {
                setLocalFields(previous);
                // error message shown by handleSaveInstallation
              } finally {
                setSaving(false);
              }
            }}
          />
        ))}
        {newFields.map(({ id, title, value }) => (
          <div key={id} className={'OtherFields__addField'}>
            <Input
              className={'OtherFields__inputName'}
              placeholder={t('nameField')}
              value={title}
              onChange={(e) =>
                setNewFields((prev) => prev.map((f) => (f.id === id ? { ...f, title: e.target.value } : f)))
              }
            />
            <div className={'OtherFields__valueField'}>
              <Input
                className={'OtherFields__input'}
                placeholder={t('value')}
                value={value}
                onChange={(e) =>
                  setNewFields((prev) => prev.map((f) => (f.id === id ? { ...f, value: e.target.value } : f)))
                }
              />
              <Button type={'text'} onClick={() => setNewFields((prev) => prev.filter((fieldId) => fieldId.id !== id))}>
                <DeleteOutlined style={{ color: colors.RED }} />
              </Button>
            </div>
          </div>
        ))}
        {(isSuperAdminOrCompanyAdmin || isAdminLocation) && (
          <>
            <Button
              onClick={() => setNewFields((prev) => [...prev, { id: crypto.randomUUID(), title: '', value: '' }])}
            >
              <PlusOutlined />
              {t('addField')}
            </Button>
            <Button type={'primary'} loading={saving} onClick={handleSave}>
              {t('save')}
            </Button>
          </>
        )}
      </div>
    </Card>
  );
};

export default OtherFieldsCard;
